import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, FileSearch } from 'lucide-react';
import { listAuditLogs, listAuditLogActions, listAuditLogActors } from '../api/auditLogs';
import { PageHeader } from '../components/ui/PageHeader';
import { Card } from '../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../components/ui/Table';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import type { AuditLogEntry } from '../types/api';

const PAGE_SIZE = 50;

function actorLabel(entry: AuditLogEntry): string {
  if (!entry.actor) return entry.actorId ? '(deleted user)' : 'System';
  const name = [entry.actor.firstName, entry.actor.lastName].filter(Boolean).join(' ');
  return name ? `${name} (${entry.actor.email})` : entry.actor.email;
}

// Renders an action string like "credit-note.issued" as a small readable
// badge rather than raw dotted-snake-case - purely cosmetic, the underlying
// filter/search still matches on the raw value.
function ActionBadge({ action }: { action: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
      {action}
    </span>
  );
}

function DetailRow({ entry }: { entry: AuditLogEntry }) {
  const hasDetail = entry.before != null || entry.after != null;
  if (!hasDetail) return null;
  return (
    <tr>
      <td colSpan={6} className="border-b border-slate-100 bg-slate-50/60 px-4 py-3 text-xs">
        <div className="grid grid-cols-2 gap-4">
          {entry.before != null && (
            <div>
              <p className="mb-1 font-semibold text-slate-500">Before</p>
              <pre className="overflow-x-auto rounded-md bg-white p-2 text-slate-700">{JSON.stringify(entry.before, null, 2)}</pre>
            </div>
          )}
          {entry.after != null && (
            <div>
              <p className="mb-1 font-semibold text-slate-500">After</p>
              <pre className="overflow-x-auto rounded-md bg-white p-2 text-slate-700">{JSON.stringify(entry.after, null, 2)}</pre>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

function LogRow({ entry }: { entry: AuditLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = entry.before != null || entry.after != null;

  return (
    <>
      <Tr className={hasDetail ? 'cursor-pointer' : undefined} onClick={() => hasDetail && setExpanded((v) => !v)}>
        <Td className="whitespace-nowrap text-slate-500">{new Date(entry.createdAt).toLocaleString()}</Td>
        <Td className="font-medium text-slate-900">{actorLabel(entry)}</Td>
        <Td>
          <ActionBadge action={entry.action} />
        </Td>
        <Td className="text-slate-500">
          {entry.entityType} <span className="font-mono text-xs text-slate-400">{entry.entityId.slice(0, 8)}</span>
        </Td>
        <Td className="font-mono text-xs text-slate-500">{entry.ipAddress ?? '-'}</Td>
        <Td className="text-right">
          {hasDetail && (expanded ? <ChevronUp className="ml-auto h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="ml-auto h-3.5 w-3.5 text-slate-400" />)}
        </Td>
      </Tr>
      {expanded && <DetailRow entry={entry} />}
    </>
  );
}

// §23: full audit log, MSP Admin only - searchable on user and transaction
// type, each entry showing date/time and IP address.
export function AuditLogPage() {
  const [actorId, setActorId] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const actorsQuery = useQuery({ queryKey: ['audit-log-actors'], queryFn: listAuditLogActors });
  const actionsQuery = useQuery({ queryKey: ['audit-log-actions'], queryFn: listAuditLogActions });

  const logsQuery = useQuery({
    queryKey: ['audit-logs', actorId, action, from, to, page],
    queryFn: () =>
      listAuditLogs({
        actorId: actorId || undefined,
        action: action || undefined,
        from: from || undefined,
        to: to ? `${to}T23:59:59.999` : undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
  });

  function resetFilters() {
    setActorId('');
    setAction('');
    setFrom('');
    setTo('');
    setPage(1);
  }

  const totalPages = logsQuery.data ? Math.max(1, Math.ceil(logsQuery.data.total / PAGE_SIZE)) : 1;

  return (
    <div>
      <PageHeader title="Audit Log" description="Every state-changing action across the platform - who, what, when, and from where." />

      <Card className="mb-6">
        <div className="flex flex-wrap items-end gap-3 p-4">
          <Select
            label="User"
            className="w-56"
            value={actorId}
            onChange={(e) => {
              setActorId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All users</option>
            {(actorsQuery.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {[a.firstName, a.lastName].filter(Boolean).join(' ') || a.email}
              </option>
            ))}
          </Select>

          <Select
            label="Transaction type"
            className="w-64"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All types</option>
            {(actionsQuery.data ?? []).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </Select>

          <Input
            label="From"
            type="date"
            className="w-40"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
          />
          <Input
            label="To"
            type="date"
            className="w-40"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
          />

          <Button variant="secondary" size="sm" onClick={resetFilters}>
            Reset
          </Button>
        </div>
      </Card>

      <Card>
        {logsQuery.isLoading ? (
          <div className="py-14 text-center">
            <Spinner />
          </div>
        ) : !logsQuery.data || logsQuery.data.items.length === 0 ? (
          <EmptyState icon={FileSearch} title="No matching entries" description="Try widening your filters." />
        ) : (
          <>
            <Table>
              <Thead>
                <tr>
                  <Th>Date / time</Th>
                  <Th>User</Th>
                  <Th>Transaction type</Th>
                  <Th>Entity</Th>
                  <Th>IP address</Th>
                  <Th />
                </tr>
              </Thead>
              <tbody>
                {logsQuery.data.items.map((entry) => (
                  <LogRow key={entry.id} entry={entry} />
                ))}
              </tbody>
            </Table>

            <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
              <span>
                {logsQuery.data.total} entr{logsQuery.data.total === 1 ? 'y' : 'ies'} - page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
